import { isEmpty, isNil } from 'lodash';
import React, { Fragment, ReactNode, useCallback, useEffect, useMemo } from 'react';
import { Easing, View as RNView, StyleProp, StyleSheet, ViewStyle } from 'react-native';
import { MBAnimation } from '@shared/components/animation';
import { MBIconV2 } from '@shared/components/image';
import { MBText } from '@shared/components/text';
import { MBTouchableOpacity } from '@shared/components/touchable';
import { EditField } from '@shared/scenes/entity/components/fields/EditField';
import { CardEntityError } from '@shared/scenes/entity/create/hooks/useFormErrors';
import { FieldRecord, UpdateField } from '@shared/scenes/entity/types';
import { getFieldError } from '@shared/scenes/entity/util/getFieldError';
import {
  isCombinedTagsComponent,
  isEntityBatchComponent,
  isLayoutComponent,
  isLayoutSection,
} from '@shared/scenes/workflows/utils';
import { grid } from '@shared/styles';
import { Colors } from '@shared/styles/colors';
import { convertViewStyleToCss } from '@shared/util/convertViewStyleToCss';
import { useIsDesktop } from '@shared/util/hooks';
import { notNil } from '@shared/util/notNil';
import { MBStorage } from '@shared/util/storage';
import { useLocalStorage } from '@shared/util/storage/useLocalStorage';
import { EntityFormProps } from './EntityFormProps';
import responsiveFormStyles from './ResponsiveFormStyles.module.css';

export function EntityFormResponsive({
  fields,
  updateField,
  components,
  errors,
  workflowTemplateId,
  entityId,
  isAuthenticated = true,
  style,
  layout,
}: EntityFormProps) {
  const isDesktop = useIsDesktop();

  const layoutItems = useMemo(() => {
    const items = layout?.items ?? [];

    // Sanitize: filter out layout components not in components, and filter
    // section componentIds to only include existing components
    const sanitizedItems = items
      .map(item => {
        if (isLayoutSection(item)) {
          const filteredIds = (item.componentIds ?? []).filter(id =>
            components.some(c => c.id === id),
          );
          return { ...item, componentIds: filteredIds } as MBAPI.ViewTemplateLayoutSection;
        }
        return item;
      })
      .filter(item => {
        if (isLayoutComponent(item)) {
          return !!item.componentId && components.some(c => c.id === item.componentId);
        }
        if (isLayoutSection(item)) {
          return (item.componentIds?.length ?? 0) > 0;
        }
        return true;
      });

    const layoutSections = sanitizedItems.filter(isLayoutSection);
    const layoutComponents = sanitizedItems.filter(isLayoutComponent);
    const otherComponents = components.filter(component => {
      const isInSection = isComponentInSections(component.id, layoutSections);
      const isStandalone = layoutComponents.some(item => item.componentId === component.id);
      const isEntityBatch = isEntityBatchComponent(component);
      const isCombinedTags = isCombinedTagsComponent(component);
      return !isInSection && !isStandalone && !isEntityBatch && !isCombinedTags;
    });
    const otherLayoutComponents = otherComponents.map(componentToLayoutComponent);
    const dedupedItems = sanitizedItems.filter(item => {
      if (isLayoutSection(item)) {
        return true;
      }
      if (isLayoutComponent(item)) {
        return !isComponentInSections(item.componentId, layoutSections);
      }
      return false;
    });

    return [...dedupedItems, ...otherLayoutComponents];
  }, [layout, components]);

  if (isNil(fields)) {
    return null;
  }

  return (
    <ClassNameView
      className={responsiveFormStyles.formContainer}
      style={[styles.formContainer, isDesktop && styles.formContainerDesktop, style]}
    >
      {layoutItems.map((item, index, list) => {
        const previousItem = list[index - 1];
        const isLastItem = index === list.length - 1;
        return (
          <Fragment key={item.id}>
            <Spacer item={item} previousItem={previousItem} />
            <LayoutItem
              item={item}
              components={components}
              fields={fields}
              workflowTemplateId={workflowTemplateId}
              updateField={updateField}
              errors={errors}
              entityId={entityId}
              isAuthenticated={isAuthenticated}
              isLastItem={isLastItem}
            />
          </Fragment>
        );
      })}
    </ClassNameView>
  );
}

function isComponentInSections(
  componentId: string | null,
  layoutSections: MBAPI.ViewTemplateLayoutSection[],
) {
  return layoutSections.some(section => section.componentIds?.includes(componentId as string));
}

function componentToLayoutComponent(
  component: MBAPI.WorkflowTemplate_components,
): MBAPI.ViewTemplateLayoutComponent {
  return {
    id: component.id,
    type: 'SINGLE_COMPONENT',
    componentId: component.id,
  };
}

function Spacer({
  item,
  previousItem,
}: {
  item: MBAPI.ViewTemplateLayout_items;
  previousItem?: MBAPI.ViewTemplateLayout_items;
}) {
  if (isNil(previousItem)) {
    return null;
  }
  if (isLayoutComponent(item) && isLayoutComponent(previousItem)) {
    return null;
  } else if (isLayoutSection(item) && isLayoutSection(previousItem)) {
    return null;
  } else if (isLayoutComponent(previousItem) && isLayoutSection(item)) {
    return <RNView style={styles.formSectionDivider}></RNView>;
  } else {
    return <RNView style={styles.sectionComponentSpacer} />;
  }
}

interface LayoutItemProps {
  item: MBAPI.ViewTemplateLayout_items;
  components: MBAPI.WorkflowTemplate_components[];
  fields: FieldRecord;
  workflowTemplateId: string;
  updateField: UpdateField;
  errors: CardEntityError[];
  entityId?: string;
  isAuthenticated: boolean;
  isLastItem: boolean;
}

function LayoutItem({
  item,
  components,
  fields,
  workflowTemplateId,
  updateField,
  errors,
  entityId,
  isAuthenticated,
  isLastItem,
}: LayoutItemProps) {
  if (isLayoutSection(item)) {
    return (
      <FormSection
        section={item}
        components={components}
        fields={fields}
        workflowTemplateId={workflowTemplateId}
        updateField={updateField}
        errors={errors}
        entityId={entityId}
        isAuthenticated={isAuthenticated}
        isLastItem={isLastItem}
      />
    );
  } else {
    const component = components.find(c => c.id === item.componentId);
    if (isNil(component) || isNil(item.componentId)) {
      return null;
    }
    return (
      <EditField
        field={fields[item.componentId]}
        fields={fields}
        workflowTemplateId={workflowTemplateId}
        component={component}
        updateField={updateField}
        error={getFieldError(errors, item.componentId)}
        entityId={entityId}
        isAuthenticated={isAuthenticated}
        style={[styles.formComponent, styles.formComponentStandalone]}
      />
    );
  }
}

interface FormSectionProps {
  section: MBAPI.ViewTemplateLayoutSection;
  components: MBAPI.WorkflowTemplate_components[];
  fields: FieldRecord;
  workflowTemplateId: string;
  updateField: UpdateField;
  errors: CardEntityError[];
  entityId?: string;
  isAuthenticated: boolean;
  isLastItem: boolean;
}

function FormSection({
  section,
  components,
  fields,
  workflowTemplateId,
  updateField,
  errors,
  entityId,
  isAuthenticated,
  isLastItem,
}: FormSectionProps) {
  const collapsedKey = `collapsed-form-section-${workflowTemplateId}-${section.id}`;
  const [isCollapsed, setIsCollapsed, isCollapsedLoading] = useLocalStorage(
    collapsedKey,
    section.isCollapsed,
  );
  const toggleCollapsed = useCallback(() => {
    setIsCollapsed(prev => !prev);
  }, [setIsCollapsed]);

  useEffect(() => {
    if (isCollapsedLoading) {
      return;
    }
    if (isCollapsed === !!section.isCollapsed) {
      MBStorage.remove(collapsedKey);
    }
  }, [collapsedKey, isCollapsed, isCollapsedLoading, section.isCollapsed]);

  const sectionComponents = useMemo(() => {
    const componentIds = section.componentIds ?? [];
    return componentIds
      .map(componentId => components.find(item => item.id === componentId))
      .filter(notNil);
  }, [components, section.componentIds]);

  if (isEmpty(sectionComponents)) {
    return null;
  }

  const isTwoColumn = section.columnCount === 2;
  const sectionClassName = [
    responsiveFormStyles.formSection,
    isTwoColumn && responsiveFormStyles.formSectionTwoColumn,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <>
      <RNView style={styles.formSectionHeader}>
        <MBTouchableOpacity onPress={toggleCollapsed} style={styles.formSectionTitle}>
          <MBAnimation
            duration={200}
            easing={Easing.inOut(Easing.quad)}
            disabled={!isCollapsed}
            rotate={{ from: '0deg', to: '-90deg' }}
          >
            <MBIconV2 color={Colors.v2.purple} name="arrow-point-down-heavy" size={grid(1.75)} />
          </MBAnimation>
          <MBText size="large" bold style={styles.formSectionTitleText} numberOfLines={1}>
            {section.title}
          </MBText>
        </MBTouchableOpacity>
      </RNView>
      {!isCollapsed && (
        <RNView style={[styles.formSection]}>
          <ClassNameView className={sectionClassName} style={styles.formSectionContent}>
            {sectionComponents.map(component => (
              <EditField
                key={component.id}
                field={fields[component.id]}
                fields={fields}
                workflowTemplateId={workflowTemplateId}
                component={component}
                updateField={updateField}
                error={getFieldError(errors, component.id)}
                entityId={entityId}
                isAuthenticated={isAuthenticated}
                style={[styles.formComponent]}
              />
            ))}
          </ClassNameView>
        </RNView>
      )}
      {isCollapsed && !isLastItem && <RNView style={styles.formSectionDivider}></RNView>}
    </>
  );
}

function ClassNameView({
  children,
  className,
  style,
}: {
  children?: ReactNode;
  className?: string;
  style?: StyleProp<ViewStyle>;
}) {
  const cssStyle = convertViewStyleToCss(style);
  return (
    <div className={className} style={cssStyle}>
      {children}
    </div>
  );
}

const styles = StyleSheet.create({
  formContainer: {
    flex: 1,
    paddingLeft: grid(1),
    paddingRight: grid(1),
    paddingTop: grid(1),
    paddingBottom: grid(4),
    backgroundColor: Colors.v2.backgroundGrayLight,
  },
  formContainerDesktop: {
    paddingTop: grid(1.5),
    paddingLeft: grid(2),
    paddingRight: grid(2),
  },
  formSectionHeader: {
    borderLeftWidth: 1,
    borderLeftColor: Colors.v2.transparent,
    alignItems: 'flex-start',
  },
  formSectionTitle: {
    paddingTop: grid(1.5),
    paddingBottom: grid(1.5),
    paddingLeft: grid(1),
    paddingRight: grid(1),
    flexDirection: 'row',
    alignItems: 'center',
    minWidth: '60%',
  },
  formSectionTitleText: {
    paddingLeft: grid(1),
    paddingRight: grid(2),
    letterSpacing: -0.1,
  },
  formSection: {
    paddingBottom: grid(3),
  },
  formSectionContent: {
    paddingLeft: grid(2),
    paddingRight: grid(2),
    paddingTop: grid(1.5),
    paddingBottom: grid(0.5),
    borderWidth: 1,
    borderStyle: 'solid',
    borderRadius: grid(1.5),
    borderColor: Colors.v2.gray20,
    backgroundColor: Colors.v2.backgroundGrayLight,
    boxShadow: '0 0 50px 0px rgba(0, 0, 0, 0.05)',
  },
  formSectionDivider: {
    marginTop: grid(2),
    marginBottom: grid(2),
    borderTopWidth: 1,
    borderStyle: 'solid',
    borderColor: Colors.v2.gray10,
  },
  formComponent: {
    paddingTop: grid(0.25),
    paddingBottom: grid(1.75),
    overflow: 'hidden',
  },
  formComponentStandalone: {
    marginLeft: grid(2),
    marginRight: grid(2),
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: Colors.v2.transparent,
  },
  sectionComponentSpacer: {
    height: grid(1.5),
  },
});
